import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import FormField from "../../../../components/common/FormField";
import { surveySummaryFields, buildSurveyServiceFields } from "./surveyUtils";
import useStyles from "./leadSurveyStyle";
import CustomButton from "../../../../components/common/button/CustomButton";
import { useSelector } from "react-redux";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  getAllRoomListAction,
  getAllSurveyedItemsAction,
  getAllSurveyedSummaryAction,
  getCompleteSurveySummaryAction,
  resetNewSurveySummaryLocal,
  setAllSurveyedItemsAction,
  setAllSurveyedSummaryAction,
  setCompleteSurveySummaryAction,
  setNewSurveySummaryLocal,
  setUpdateSurveySummaryAction,
  updateSurveySummaryAction,
} from "../../../../store/reducers/leadSurveySlice";
import { useDispatch } from "react-redux";
import Validation from "../../../../utils/Validation";
import Notification from "../../../../components/common/notification/Notification";

import {
  checkValidPostcodeAction,
  getUploadKeyAction,
  setCheckValidPostcodeAction,
  setNotificationAction,
  setUploadFileAction,
} from "../../../../store/reducers/mmymSlice";
import AlertCompleteSurvey from "./AlertCompleteSurvey";
import dayjs from "dayjs";
import CollectionSummaryForm from "./summary/CollectionSummaryForm";
import DeliverySummaryForm from "./summary/DeliverySummaryForm";
import SelectedSurveyItem from "./summary/SelectedSurveyItem";
import { getDefaultServiceListAction } from "../../../../store/reducers/reviewSlice";

type Props = {
  show: any;
  setShow: any;
  disabledAction?: boolean;
};

type PendingPhoto = {
  tempId: string;
  file: File;
  previewUrl: string;
};

const SurveyedSummaryTab = forwardRef<any, Props>(
  ({ show, setShow, disabledAction = false }, ref) => {
    const classes = useStyles({ show }); // Pass the required argument to useStyles
    const dispatch = useDispatch();
    const { leadDetails } = useSelector((state: any) => state.lead);
    const { userProfile, user } = useSelector((state: any) => state.user);
    const { isValidPostcode, imageUploadKey, publicUrl } = useSelector(
      (state: any) => state.mmym,
    );
    const { defaultServiceList = [] } = useSelector(
      (state: any) => state.review,
    );
    const [isProfessionalSection, setIsProfessionalSection] = useState(
      user?.user?.is_professional_plan ||
        userProfile?.features?.includes("Professional Financial") ||
        false, // Safe fallback
    );
    const {
      surveyedSummary,
      newSurveySummary,
      isSurveySummarySuccess,
      isCompleteSurveyedSummarySuccess,
      surveyRoomList,
    } = useSelector((state: any) => state.leadSurvey);
    const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const handledUploadKeyRef = useRef("");
    const photoInputRef = useRef<HTMLInputElement | null>(null);

    const [
      error,
      checkValidator,
      resetValidator,
      resetAllValidator,
      isError,
    ]: any = Validation({
      removalDate: newSurveySummary?.removalDate,
      summaryStatus: newSurveySummary?.summaryStatus,
      street: newSurveySummary?.movingFrom[0]?.street,
      // movingToStreet: newSurveySummary?.movingToStreet,
      town: newSurveySummary?.movingFrom[0]?.town,
      // movingToTown: newSurveySummary?.movingToTown,
      postcode: newSurveySummary?.movingFrom[0]?.postcode,
      // movingToPostcode: newSurveySummary?.movingToPostcode,
    });

    const [isActionCalled, setIsActionCalled] = useState(false);
    const [postCodeSelected, setPostCodeSelected] = useState("");
    const [countryCode, setCountryCode] = useState<string>("+44");
    const [alternateCountryCode, setAlternateCountryCode] =
      useState<string>("+44");
    const [queuedPhotoFiles, setQueuedPhotoFiles] = useState<PendingPhoto[]>(
      [],
    );
    const [activePhotoUpload, setActivePhotoUpload] =
      useState<PendingPhoto | null>(null);
    const [photoUploadError, setPhotoUploadError] = useState("");

    const getPreviewUrlFromPhoto = (photo: any) => {
      return (
        photo?.url ||
        photo?.publicUrl ||
        photo?.signedUrl ||
        photo?.signed_url ||
        photo?.photo_url ||
        (typeof photo?.key === "string" && photo.key.startsWith("http")
          ? photo.key
          : "")
      );
    };

    const sanitizeFileName = (fileName: string) => {
      return fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
    };

    const getLimitedUploadFileName = (file: File) => {
      const surveyId = String(leadDetails?.lead?.survey_id || "survey");
      const basePrefix = `survey/${surveyId}/`;
      const extensionMatch = file.name.match(/\.[^.]+$/);
      const extension = extensionMatch ? extensionMatch[0] : "";
      const timestampPrefix = `${Date.now()}-`;
      const cleanedName = sanitizeFileName(file.name.replace(/\.[^.]+$/, ""));
      const fixedLength =
        basePrefix.length + timestampPrefix.length + extension.length;
      const allowedNameLength = Math.max(1, 140 - fixedLength);
      const truncatedName = cleanedName.slice(0, allowedNameLength);

      return `${basePrefix}${timestampPrefix}${truncatedName}${extension}`;
    };

    useEffect(() => {
      dispatch(getAllSurveyedSummaryAction(leadDetails?.lead?.survey_id));
      dispatch(getAllRoomListAction(leadDetails?.lead?.generated_id));
      dispatch(getDefaultServiceListAction());
      return () => {
        dispatch(setNotificationAction({ message: [], severity: "info" }));
        dispatch(setCheckValidPostcodeAction(false));
        dispatch(setAllSurveyedSummaryAction(null));
        dispatch(resetNewSurveySummaryLocal());
      };
    }, []);

    useEffect(() => {
      if (surveyedSummary) {
        const surveyData = surveyedSummary.lead;
        const surveyDetails = surveyedSummary.survey;

        setCountryCode(
          surveyData?.customerCountryCode
            ? surveyData?.customerCountryCode
            : "+44",
        );
        setAlternateCountryCode(
          surveyData?.customerAlternateCountryCode
            ? surveyData?.customerAlternateCountryCode
            : "+44",
        );

        dispatch(
          setNewSurveySummaryLocal({
            // removalDate: surveyData?.removalDate || newSurveySummary?.removalDate,
            removalDate:
              surveyData?.removalEndDate || newSurveySummary?.removalEndDate
                ? [
                    surveyData?.removalDate || newSurveySummary?.removalDate,
                    surveyData?.removalEndDate ||
                      newSurveySummary?.removalEndDate,
                  ].filter(Boolean)
                : surveyData?.removalDate || newSurveySummary?.removalDate,
            removalEndDate:
              surveyData?.removalEndDate || newSurveySummary?.removalEndDate,
            movingFrom: surveyData?.moving_from
              ? surveyData.moving_from.map((item: any) => ({
                  ...item,
                  purchaseStatus: item?.purchase_status,
                  flightOfStairs: item?.flight_of_stairs,
                  isPostcodeVerified: item?.is_postcode_verified ?? false,
                  liftAvailability: item?.lift_availability,
                  volumeUnit: item?.volume_unit,
                  houseSize: item?.house_size,
                }))
              : newSurveySummary?.movingFrom,
            movingTo: surveyData?.moving_to
              ? surveyData.moving_to.map((item: any) => ({
                  ...item,
                  purchaseStatus: item?.purchase_status,
                  flightOfStairs: item?.flight_of_stairs,
                  isPostcodeVerified: item?.is_postcode_verified ?? false,
                  liftAvailability: item?.lift_availability,
                  houseSize: item?.house_size,
                }))
              : newSurveySummary?.movingTo,
            customerId: surveyData?.customerId || newSurveySummary?.customerId,
            customerName:
              surveyData?.customerName || newSurveySummary?.customerName,
            customerCountryCode: surveyData?.customerCountryCode
              ? surveyData?.customerCountryCode
              : "+44",
            customerPhone:
              surveyData?.customerPhone?.replace(/^(\+91|\+44)/, "") ||
              newSurveySummary?.customerPhone?.replace(/^(\+91|\+44)/, ""),
            customerAlternateCountryCode:
              surveyData?.customerAlternateCountryCode || "+44",
            customerAlternatePhone:
              surveyData?.customerAlternatePhone?.replace(/^(\+91|\+44)/, "") ||
              newSurveySummary?.customerAlternatePhone?.replace(
                /^(\+91|\+44)/,
                "",
              ),
            access: surveyDetails?.access || newSurveySummary?.access,
            customerEmail:
              surveyData?.customerEmail || newSurveySummary?.customerEmail,
            photos: (
              surveyDetails?.photos ||
              newSurveySummary?.photos ||
              []
            ).map((photo: any) => ({
              id: photo?.id || "",
              key: photo?.key || "",
              url: getPreviewUrlFromPhoto(photo),
            })),
            notes: surveyDetails?.notes,
            summaryStatus: surveyDetails?.summaryStatus
              ? surveyDetails?.summaryStatus?.toLowerCase()
              : null,
            contentValue: Number(surveyDetails?.contentValue),
            services: surveyDetails?.services || newSurveySummary?.services,
            requirements:
              surveyDetails?.requirements || newSurveySummary?.requirements,
          }),
        );
      }
    }, [surveyedSummary, dispatch]);

    useEffect(() => {
      if (isValidPostcode) {
        if (
          newSurveySummary?.movingFromPostcode?.trim() !== "" &&
          postCodeSelected === "movingFromPostcode"
        ) {
          dispatch(
            setNewSurveySummaryLocal({
              ...newSurveySummary,
              // isMovingFromPostcodeVerified: isValidPostcode,
            }),
          );
        } else if (
          newSurveySummary?.movingToPostcode?.trim() !== "" &&
          postCodeSelected === "movingToPostcode"
        ) {
          dispatch(
            setNewSurveySummaryLocal({
              ...newSurveySummary,
              // isMovingToPostcodeVerified: isValidPostcode,
            }),
          );
        }
        dispatch(setCheckValidPostcodeAction(false));
      }
    }, [
      isValidPostcode,
      newSurveySummary?.movingFromPostcode,
      newSurveySummary?.movingToPostcode,
      dispatch,
    ]);

    // Hardcoded requirement field ids — everything else that is a radio is a service
    const REQUIREMENT_IDS = new Set(["insurance", "material", "fragile"]);
    const NON_SERVICE_IDS = new Set([
      "notes",
      "removalDate",
      "summaryStatus",
      "contentValue",
      "customerName",
      "customerPhone",
      "customerEmail",
      "customerAlternatePhone",
      "movingFromPostcode",
      "movingToPostcode",
      "isMovingFromPostcodeVerified",
      "isMovingToPostcodeVerified",
    ]);

    const SERVICE_TYPE_ALIASES: Record<string, string> = {
      fullPack: "fullpack",
      exportPack: "exportpack",
    };

    const normalizeServiceType = (serviceType: string) => {
      return SERVICE_TYPE_ALIASES[serviceType] || serviceType;
    };

    const getServiceDisplayName = (serviceType: string) => {
      const normalizedServiceType = normalizeServiceType(serviceType);

      return (
        defaultServiceList?.find(
          (service: any) =>
            normalizeServiceType(service?.type) === normalizedServiceType,
        )?.name || serviceType
      );
    };

    const getServiceDescription = (serviceType: string) => {
      const normalizedServiceType = normalizeServiceType(serviceType);

      return (
        defaultServiceList?.find(
          (service: any) =>
            normalizeServiceType(service?.type) === normalizedServiceType,
        )?.description || ""
      );
    };

    const getRequirementDisplayName = (requirementType: string) => {
      if (requirementType === "insurance") return "Insurance";
      if (requirementType === "material") return "Material";
      return requirementType;
    };

    const normalizeSurveyServices = (services: any[] = []) => {
      const normalizedServiceMap = new Map<string, any>();
      const allowedServiceTypes = new Set(
        (defaultServiceList || []).map((service: any) =>
          normalizeServiceType(service?.type || ""),
        ),
      );
      const hasDefaultServiceList = allowedServiceTypes.size > 0;

      services.forEach((service: any) => {
        const normalizedServiceName = normalizeServiceType(
          service?.serviceName || "",
        );

        if (!normalizedServiceName) {
          return;
        }

        // Once default services are loaded, only keep services that exist in that list.
        if (
          hasDefaultServiceList &&
          !allowedServiceTypes.has(normalizedServiceName)
        ) {
          return;
        }

        normalizedServiceMap.set(normalizedServiceName, {
          ...service,
          serviceName: normalizedServiceName,
          typeName: getServiceDisplayName(normalizedServiceName),
          description: getServiceDescription(normalizedServiceName),
        });
      });

      return Array.from(normalizedServiceMap.values());
    };

    const normalizeSurveyRequirements = (requirements: any[] = []) => {
      return requirements.map((requirement: any) => ({
        ...requirement,
        typeName: getRequirementDisplayName(requirement?.serviceName),
      }));
    };

    // Handle input changes
    const onChangeAction = (event: any, valueFormat?: any) => {
      const { id, value, checked } = event.target;
      const normalizedId = normalizeServiceType(id);

      const isDynamicService =
        !REQUIREMENT_IDS.has(normalizedId) &&
        !NON_SERVICE_IDS.has(normalizedId) &&
        defaultServiceList?.some(
          (svc: any) => normalizeServiceType(svc.type) === normalizedId,
        );

      if (isDynamicService) {
        // Convert "true"/"false" string to actual boolean
        const isAccepted = value === "true";
        const typeName = getServiceDisplayName(normalizedId);
        const updatedServices = newSurveySummary?.services
          ? [
              ...newSurveySummary?.services.map((service: any) => ({
                ...service,
              })),
            ]
          : [];

        const serviceIndex = updatedServices.findIndex(
          (service) =>
            normalizeServiceType(service.serviceName) === normalizedId,
        );

        if (serviceIndex >= 0) {
          // If the service exists, update the isAccepted status
          updatedServices[serviceIndex].isAccepted = isAccepted;
          updatedServices[serviceIndex].serviceName = normalizedId;
          updatedServices[serviceIndex].typeName = typeName;
        } else {
          // If the service does not exist, add it
          updatedServices.push({
            serviceName: normalizedId,
            typeName,
            isAccepted: isAccepted,
          });
        }

        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            services: normalizeSurveyServices(updatedServices),
            // requirements : updatedRequirements
          }),
        );
      } else if (id === "material" || id === "insurance") {
        // Convert "true"/"false" string to actual boolean
        const isAccepted = value === "true";
        const typeName = getRequirementDisplayName(id);
        const updatedRequirements = newSurveySummary?.requirements
          ? [
              ...newSurveySummary?.requirements.map((requirement: any) => ({
                ...requirement,
              })),
            ]
          : [];

        const rquirementIndex = updatedRequirements.findIndex(
          (service) => service.serviceName === id,
        );

        if (rquirementIndex >= 0) {
          // If the service exists, update the isAccepted status
          updatedRequirements[rquirementIndex].isAccepted = isAccepted;
          updatedRequirements[rquirementIndex].typeName = typeName;
        } else {
          // If the service does not exist, add it
          updatedRequirements.push({
            serviceName: id,
            typeName,
            isAccepted: isAccepted,
          });
        }

        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            requirements: updatedRequirements,
          }),
        );
      } else if (
        id === "isMovingFromPostcodeVerified" ||
        id === "isMovingToPostcodeVerified"
      ) {
        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            [id]: checked,
          }),
        );
      } else if (id === "removalDate") {
        if (Array.isArray(value)) {
          dispatch(
            setNewSurveySummaryLocal({
              ...newSurveySummary,
              [id]: value[0],
              removalEndDate: value[1],
            }),
          );
        } else {
          dispatch(
            setNewSurveySummaryLocal({
              ...newSurveySummary,
              [id]: value,
              removalEndDate: value,
            }),
          );
        }
      } else {
        // Handle other input changes
        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            [id]:
              id === "movingFromPostcode" || id === "movingToPostcode"
                ? value
                  ? value.toUpperCase()
                  : value
                : value,
          }),
        );
      }
      resetValidator(id);
    };

    const [isPostCodeActionCalled, setIsPostCodeActionCalled] = useState(false);

    // Handle blur action
    const onBlurAction = (e: any) => {
      checkValidator(e.target.id);
      if (
        (e.target.id === "movingFromPostcode" ||
          e.target.id === "movingToPostcode") &&
        e.target.value.trim() !== ""
      ) {
        setIsPostCodeActionCalled(true);
        setPostCodeSelected(e.target.id);
      }
    };

    useEffect(() => {
      if (isPostCodeActionCalled) {
        if (newSurveySummary?.movingFromPostcode?.length > 4) {
          dispatch(
            checkValidPostcodeAction(newSurveySummary?.movingFromPostcode),
          );
        } else if (newSurveySummary?.movingToPostcode?.length >= 4) {
          dispatch(
            checkValidPostcodeAction(newSurveySummary?.movingToPostcode),
          );
        } else {
          dispatch(setCheckValidPostcodeAction(false));
        }
        setIsPostCodeActionCalled(false);
      }
    }, [
      isPostCodeActionCalled,
      newSurveySummary?.movingFromPostcode,
      newSurveySummary?.movingToPostcode,
      dispatch,
    ]);

    const [alerModeShow, setAlertModelShow] = useState(false);
    const [isClickReset, setIsClickReset] = useState(false);
    const [isCompletedRole, setIsCompletedRole] = useState(false);

    const totalPhotoCount = (newSurveySummary?.photos || []).length;

    const handlePhotoSelection = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const selectedFiles = Array.from(event.target.files || []);

      if (!selectedFiles.length) {
        return;
      }

      const existingCount = (newSurveySummary?.photos || []).length;
      const availableSlots = Math.max(0, 20 - existingCount);

      if (availableSlots <= 0) {
        dispatch(
          setNotificationAction({
            message: ["Maximum 20 images are allowed"],
            severity: "error",
          }),
        );
        event.target.value = "";
        return;
      }

      const validFiles: PendingPhoto[] = [];
      const errors: string[] = [];

      selectedFiles.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          errors.push(`${file.name} is not an image file.`);
          return;
        }

        if (file.size > 5000000) {
          errors.push(`${file.name} is too large. Maximum size is 5MB.`);
          return;
        }

        if (validFiles.length < availableSlots) {
          validFiles.push({
            tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            previewUrl: URL.createObjectURL(file),
          });
        }
      });

      if (selectedFiles.length > availableSlots) {
        errors.push("Only 20 images can be uploaded in total.");
      }

      if (errors.length) {
        setPhotoUploadError(errors.join(" "));
        dispatch(
          setNotificationAction({
            message: errors,
            severity: "error",
          }),
        );
      }

      if (validFiles.length) {
        setPhotoUploadError("");
        setQueuedPhotoFiles((prev) => [...prev, ...validFiles]);
        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            photos: [
              ...(newSurveySummary?.photos || []),
              ...validFiles.map((item) => ({
                id: "",
                key: "",
                tempId: item.tempId,
                url: item.previewUrl,
                name: item.file.name,
                isUploading: true,
              })),
            ],
          }),
        );
      }

      event.target.value = "";
    };

    const handleRemovePhoto = (photoIndex: number) => {
      const nextPhotos = [...(newSurveySummary?.photos || [])];
      const removed = nextPhotos[photoIndex];

      if (removed?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.url);
      }

      nextPhotos.splice(photoIndex, 1);

      if (removed?.tempId) {
        setQueuedPhotoFiles((prev) =>
          prev.filter((item) => item.tempId !== removed.tempId),
        );
      }

      dispatch(
        setNewSurveySummaryLocal({
          ...newSurveySummary,
          photos: nextPhotos,
        }),
      );
    };

    const handleOpenPhotoDialog = () => {
      photoInputRef.current?.click();
    };

    useEffect(() => {
      if (!activePhotoUpload && queuedPhotoFiles.length > 0) {
        const nextFile = queuedPhotoFiles[0];
        handledUploadKeyRef.current = "";
        setActivePhotoUpload(nextFile);
        dispatch(
          getUploadKeyAction({
            fileName: getLimitedUploadFileName(nextFile.file),
            fileType: nextFile.file.type,
            body: nextFile.file,
            page: "survey",
            keepPublic: false,
          }),
        );
      }
    }, [
      activePhotoUpload,
      dispatch,
      leadDetails?.lead?.survey_id,
      queuedPhotoFiles,
    ]);

    useEffect(() => {
      if (
        imageUploadKey?.key &&
        activePhotoUpload &&
        handledUploadKeyRef.current !== imageUploadKey.key
      ) {
        handledUploadKeyRef.current = imageUploadKey.key;
        const updatedPhotos = [...(newSurveySummary?.photos || [])];
        const idx = updatedPhotos.findIndex(
          (photo: any) => photo?.tempId === activePhotoUpload.tempId,
        );

        if (idx !== -1) {
          const serverUrl =
            publicUrl || imageUploadKey?.url || updatedPhotos[idx]?.url || "";
          updatedPhotos[idx] = {
            ...updatedPhotos[idx],
            key: imageUploadKey.key,
            url: serverUrl,
            isUploading: false,
          };
        }

        dispatch(
          setNewSurveySummaryLocal({
            ...newSurveySummary,
            photos: updatedPhotos,
          }),
        );

        setQueuedPhotoFiles((prev) => prev.slice(1));
        setActivePhotoUpload(null);
        setPhotoUploadError("");
        dispatch(
          setUploadFileAction({
            url: "",
            key: "",
            publicUrl: "",
          }),
        );
      }
    }, [
      activePhotoUpload,
      dispatch,
      imageUploadKey?.key,
      imageUploadKey?.url,
      newSurveySummary,
      publicUrl,
    ]);

    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (queuedPhotoFiles.length > 0 || activePhotoUpload) {
        dispatch(
          setNotificationAction({
            message: ["Please wait until image upload completes"],
            severity: "error",
          }),
        );
        return;
      }
      checkValidator();
      setIsActionCalled(true);
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: () => handleSubmit(), // call without event
    }));

    useEffect(() => {
      if (isActionCalled) {
        const processedLeadValue = {
          ...newSurveySummary,
          movingFrom: (newSurveySummary?.movingFrom || []).map((item: any) => ({
            ...item,
            flightOfStairs: Number(item?.flightOfStairs),
            paces: Number(item?.paces),
            floors: Number(item?.floors),
            houseSize: Number(item?.houseSize),
            bedrooms: Number(item?.bedrooms),
            volume_unit: item?.volumeUnit,
          })),
          movingTo: (newSurveySummary?.movingTo || []).map((item: any) => ({
            ...item,
            flightOfStairs: Number(item?.flightOfStairs),
            paces: Number(item?.paces),
            floors: Number(item?.floors),
            houseSize: Number(item?.houseSize),
            bedrooms: Number(item?.bedrooms),
          })),
          customerCountryCode: countryCode ? countryCode : "+44",
          customerPhone: newSurveySummary?.customerPhone
            ? newSurveySummary?.customerPhone.replace(/^(\+91|\+44)/, "")
            : "",
          customerAlternateCountryCode: alternateCountryCode
            ? alternateCountryCode
            : "+44",
          customerAlternatePhone: newSurveySummary?.customerAlternatePhone
            ? newSurveySummary?.customerAlternatePhone.replace(
                /^(\+91|\+44)/,
                "",
              )
            : "",
          photos: (newSurveySummary?.photos || [])
            .map((photo: any) => ({
              id: photo?.id || "",
              key: photo?.key || "",
            }))
            .filter((photo: any) => Boolean(photo?.key)),
          services: normalizeSurveyServices(newSurveySummary?.services || []),
          requirements: normalizeSurveyRequirements(
            newSurveySummary?.requirements || [],
          ),
          access: newSurveySummary?.access ? newSurveySummary?.access : null,
          removalDate: Array.isArray(newSurveySummary?.removalDate)
            ? newSurveySummary?.removalDate[0]
            : newSurveySummary?.removalDate,
          removalEndDate: Array.isArray(newSurveySummary?.removalDate)
            ? newSurveySummary?.removalDate[1]
            : newSurveySummary?.removalDate,

          contentValue: Number(newSurveySummary?.contentValue),
        };

        console.log("Processed Lead Value:", processedLeadValue);
        if (!isError() && error !== null && isActionCalled) {
          dispatch(
            updateSurveySummaryAction({
              ...processedLeadValue,
              surveyId: leadDetails?.lead?.survey_id,
            }),
          );
        } else {
          // Small delay to ensure error state updates before scrolling
          setTimeout(() => {
            if (error && typeof error === "object") {
              const firstErrorKey = Object.keys(error).find(
                (key) => error[key],
              );

              if (firstErrorKey) {
                const firstErrorElement = fieldRefs.current[firstErrorKey];
                if (firstErrorElement) {
                  firstErrorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  if (typeof firstErrorElement.focus === "function") {
                    firstErrorElement.focus();
                  }
                }
              }
            }
          }, 100);
        }
        setIsActionCalled(false);
      }
    }, [isActionCalled]);

    const handleCompleteSurvey = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (queuedPhotoFiles.length > 0 || activePhotoUpload) {
        dispatch(
          setNotificationAction({
            message: ["Please wait until image upload completes"],
            severity: "error",
          }),
        );
        return;
      }
      checkValidator();
      setIsActionCalled(true);
      setIsCompletedRole(true);
    };

    useEffect(() => {
      if (isSurveySummarySuccess && isCompletedRole) {
        setAlertModelShow(true);
        dispatch(getAllSurveyedSummaryAction(leadDetails?.lead?.survey_id));
        dispatch(setUpdateSurveySummaryAction(false));
      } else if (isSurveySummarySuccess && !isCompletedRole) {
        dispatch(getAllSurveyedSummaryAction(leadDetails?.lead?.survey_id));
        dispatch(setUpdateSurveySummaryAction(false));
      }
    }, [isSurveySummarySuccess]);

    const getServiceValue = (services: any, id: any) => {
      const filter = services?.find(
        (ele: any) =>
          normalizeServiceType(ele?.serviceName) === normalizeServiceType(id),
      );
      if (filter) {
        return filter?.isAccepted;
      }
    };

    const getRequirementValue = (requirements: any, id: any) => {
      const filter = requirements?.find((ele: any) => ele?.serviceName === id);
      if (filter) {
        return filter?.isAccepted;
      }
    };

    useEffect(() => {
      if (isClickReset) {
        dispatch(getCompleteSurveySummaryAction(leadDetails?.lead?.survey_id));
      }
      setIsClickReset(false);
    }, [isClickReset]);

    useEffect(() => {
      if (isCompleteSurveyedSummarySuccess) {
        setAlertModelShow(false);
        dispatch(setCompleteSurveySummaryAction(false));
        dispatch(getAllSurveyedItemsAction(leadDetails?.lead?.survey_id));
        setShow(false);
        dispatch(setAllSurveyedItemsAction(null));
      }
      setIsCompletedRole(false);
    }, [isCompleteSurveyedSummarySuccess]);

    return (
      <>
        {alerModeShow && (
          <AlertCompleteSurvey
            open={alerModeShow}
            title="Complete Survey?"
            alertModalShow={alerModeShow}
            setAlertModelShow={setAlertModelShow}
            setIsClickReset={setIsClickReset}
            setIsCompletedRole={setIsCompletedRole}
          />
        )}
        <section
          style={{
            backgroundColor: "#fff",
            padding: "0px",
            pointerEvents:
              leadDetails?.lead?.status === "COMPLETED" ? "none" : "auto",
            marginTop: "5px",
          }}
          className={classes.surveySummaryPopupContainer}
        >
          <Notification />
          <Grid
            container
            spacing={1}
            sx={{
              marginTop: "10px !important",
              overflow: "hidden",
              borderRadius: "5px",
              border: "1px solid #CFCFCF",
            }}
          >
            <Grid
              item
              md={12}
              sx={{
                padding: "10px 10px 10px 0px !important",
                background: "#E5F4F1",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                REF:{surveyedSummary && surveyedSummary?.lead?.leadId} | SURVEY
                DATE:{" "}
                {surveyedSummary &&
                  dayjs(surveyedSummary?.survey?.startTime).format("llll")}{" "}
                | Client Name:{" "}
                {surveyedSummary && surveyedSummary?.lead?.customerName}
              </Typography>
            </Grid>
            {surveySummaryFields?.map((field, i) => {
              return (
                <>
                  {field?.type === "checkbox" ? (
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        paddingTop: "0px !important",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={disabledAction}
                            checked={
                              newSurveySummary
                                ? newSurveySummary[field?.id]
                                : ""
                            }
                            onChange={(e: any) => onChangeAction(e)}
                            name={field?.id}
                            id={field?.id}
                            color="primary"
                            inputRef={(el: any) => {
                              fieldRefs.current[field.id] = el;
                            }}
                          />
                        }
                        label="Verified"
                      />
                    </Grid>
                  ) : (
                    <FormField
                      key={i}
                      inputRef={(el: any) => {
                        fieldRefs.current[field.id] = el;
                      }}
                      onPaste={(e: any) =>
                        field?.type === "text" ? "" : e.preventDefault()
                      }
                      onKeyDown={(event: any) => {
                        if (
                          event.keyCode === 8 ||
                          event.keyCode === 9 ||
                          event.keyCode === 37 ||
                          event.keyCode === 38 ||
                          event.keyCode === 39 ||
                          event.keyCode === 40 ||
                          event.keyCode === 18 ||
                          event.keyCode === 17 ||
                          event.ctrlKey
                        )
                          return;

                        if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(event.key)
                        ) {
                          event.preventDefault();
                        } else if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(
                            event.target.value + event.key,
                          )
                        ) {
                          event.preventDefault();
                        }
                      }}
                      {...{
                        ...field,
                        multiline: true,
                        minDate:
                          field.id === "removalDate" ? new Date() : undefined,
                        editReadOnly: disabledAction,
                        GridFieldStyle: {
                          paddingRight:
                            field?.type === "contact" ? "10px" : "6px",
                          paddingLeft:
                            i % 2 !== 0 ? "2px !important" : undefined,
                        },
                        disabled:
                          field?.id === "removal" ? true : disabledAction,
                        marginTopCountryCode: "2px",
                        onCountryCodeChange:
                          field.type === "contact" // Example of checking if it's a country code field
                            ? (code: string) => {
                                if (field?.id === "customerAlternatePhone") {
                                  setAlternateCountryCode(code);
                                } else {
                                  setCountryCode(code);
                                }
                              }
                            : undefined,
                        ...{
                          ...(field?.type === "contact" && {
                            countryCode:
                              field?.id === "customerAlternatePhone"
                                ? alternateCountryCode ||
                                  newSurveySummary?.customerAlternateCountryCode ||
                                  ""
                                : countryCode ||
                                  newSurveySummary?.customerCountryCode ||
                                  "",
                          }),
                        },
                        value: newSurveySummary
                          ? field?.id === "insurance" ||
                            field?.id === "material" ||
                            field?.id === "fragile"
                            ? getRequirementValue(
                                newSurveySummary?.requirements,
                                field?.id,
                              )
                            : field?.id === "customerPhone"
                              ? newSurveySummary[field?.id]?.replace(
                                  /^(\+91|\+44)/,
                                  "",
                                )
                              : field?.id === "customerAlternatePhone"
                                ? newSurveySummary[field?.id]?.replace(
                                    /^(\+91|\+44)/,
                                    "",
                                  )
                                : newSurveySummary[field?.id]
                          : "",

                        ...{
                          variant: "outlined", // Adding this variant prop
                          onChange: onChangeAction,
                          onBlur: onBlurAction,
                          isNonMarginTop: true,
                          inSingleGridForm: true,
                          InputProps: {
                            ...(field?.inputProps && field?.inputProps),
                            ...{
                              disabled: field?.editReadOnly || disabledAction,
                            },

                            sx:
                              field?.type === "text" ||
                              field?.type === "password"
                                ? {
                                    height: "36px",
                                    padding: "10px 0px 6px 4px",
                                  }
                                : field?.type === "contact"
                                  ? {
                                      height: "36px",
                                      padding: "10px 4px 4px 4px !important",
                                    }
                                  : { padding: "10px 4px", height: "36px" },
                          },
                        },
                        error: error && error[field?.id],
                      }}
                    />
                  )}
                </>
              );
            })}
          </Grid>
          <Grid container spacing={1} sx={{ marginTop: "10px !important" }}>
            {/* collection address */}
            <Grid
              item
              md={12}
              sx={{ padding: "0px !important", marginTop: "10px" }}
            >
              <CollectionSummaryForm
                resetValidator={resetValidator}
                checkValidator={checkValidator}
                error={error}
                disabledAction={disabledAction}
                setCountryCode={setCountryCode}
                countryCode={countryCode}
                inputRefMap={fieldRefs} // ✅ pass this
              />
            </Grid>

            {/* delivery address */}
            <Grid
              item
              md={12}
              sx={{ padding: "0px !important", marginTop: "4px" }}
            >
              <DeliverySummaryForm
                // resetValidator={resetValidator}
                // checkValidator={checkValidator}
                // error={error}
                disabledAction={disabledAction}
                setCountryCode={setCountryCode}
                countryCode={countryCode}
              />
            </Grid>
          </Grid>
          {/* service list */}
          <Grid
            container
            spacing={1}
            sx={{
              marginTop: "0px !important",
              borderRadius: "5px",
              border: "1px solid #CFCFCF",
              background: "#E5F4F1",
              padding: "10px",
            }}
          >
            {buildSurveyServiceFields(
              defaultServiceList,
              isProfessionalSection,
              user?.user?.roles?.[0] === "CUSTOMER",
            )?.map((field, i) => {
              return (
                <>
                  {field?.type === "checkbox" ? (
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        paddingTop: "0px !important",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={disabledAction}
                            checked={
                              newSurveySummary
                                ? newSurveySummary[field?.id]
                                : ""
                            }
                            onChange={(e: any) => onChangeAction(e)}
                            name={field?.id}
                            id={field?.id}
                            color="primary"
                          />
                        }
                        label="Verified"
                      />
                    </Grid>
                  ) : (
                    <FormField
                      key={i}
                      onPaste={(e: any) =>
                        field?.type === "textarea" ? "" : e.preventDefault()
                      }
                      onKeyDown={(event: any) => {
                        if (
                          event.keyCode === 8 ||
                          event.keyCode === 9 ||
                          event.keyCode === 37 ||
                          event.keyCode === 38 ||
                          event.keyCode === 39 ||
                          event.keyCode === 40 ||
                          event.keyCode === 18 ||
                          event.keyCode === 17 ||
                          event.ctrlKey
                        )
                          return;
                      }}
                      {...{
                        ...field,
                        multiline: true,
                        minDate:
                          field.id === "removalDate" ? new Date() : undefined,
                        editReadOnly: disabledAction,
                        disabled:
                          field?.id === "removal" ? true : disabledAction,
                        marginTopCountryCode: "2px",
                        onCountryCodeChange:
                          field.type === "contact" // Example of checking if it's a country code field
                            ? (code: string) => setCountryCode(code)
                            : undefined,
                        ...{
                          ...(field?.type === "contact" && {
                            countryCode: newSurveySummary[field.id]
                              ? countryCode
                              : "",
                          }),
                        },
                        value: newSurveySummary
                          ? (field as any)?.isService
                            ? getServiceValue(
                                newSurveySummary?.services,
                                field?.id,
                              )
                            : (field as any)?.isRequirement
                              ? getRequirementValue(
                                  newSurveySummary?.requirements,
                                  field?.id,
                                )
                              : newSurveySummary[field?.id]
                          : "",

                        ...{
                          variant: "outlined", // Adding this variant prop
                          onChange: onChangeAction,
                          onBlur: onBlurAction,
                          isNonMarginTop: true,
                          InputProps: {
                            ...((field as any)?.inputProps &&
                              (field as any)?.inputProps),
                            ...{
                              disabled:
                                (field as any)?.editReadOnly || disabledAction,
                            },

                            sx:
                              field?.type === "text" ||
                              field?.type === "password"
                                ? {
                                    height: "36px",
                                    padding: "10px 0px 6px 4px",
                                  }
                                : field?.type === "contact"
                                  ? {
                                      height: "36px",
                                      padding: "16px 0px 6px 4px !important",
                                    }
                                  : { padding: "10px 4px", height: "36px" },
                          },
                        },
                        error: error && error[field?.id],
                      }}
                    />
                  )}
                </>
              );
            })}
          </Grid>
          <Grid
            container
            spacing={1}
            sx={{
              marginTop: "10px !important",
              borderRadius: "5px",
              border: "1px solid #CFCFCF",
              background: "#fff",
              padding: "12px",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Survey Photos ({(newSurveySummary?.photos || []).length}/20)
              </Typography>
              {!disabledAction && (
                <>
                  <input
                    id="survey-photo-upload"
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handlePhotoSelection}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpenPhotoDialog}
                    disabled={
                      Boolean(activePhotoUpload) || totalPhotoCount >= 20
                    }
                    sx={{
                      textTransform: "capitalize",
                      height: "30px",
                    }}
                  >
                    {activePhotoUpload ? "Uploading..." : "Upload Images"}
                  </Button>
                </>
              )}
              {photoUploadError ? (
                <Alert sx={{ mt: 1 }} severity="error">
                  {photoUploadError}
                </Alert>
              ) : null}
              {queuedPhotoFiles.length > 0 ? (
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {queuedPhotoFiles.length} image(s) waiting to upload
                </Typography>
              ) : null}
            </Grid>
            {(newSurveySummary?.photos || []).length > 0 && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(120px, 1fr))",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {(newSurveySummary?.photos || []).map(
                    (photo: any, index: number) => (
                      <Box
                        key={`${photo?.key || "photo"}-${index}`}
                        sx={{
                          border: "1px solid #CFCFCF",
                          borderRadius: "6px",
                          p: 1,
                          position: "relative",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        {getPreviewUrlFromPhoto(photo) ? (
                          <img
                            src={getPreviewUrlFromPhoto(photo)}
                            alt={`survey-photo-${index + 1}`}
                            style={{
                              width: "100%",
                              height: "90px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: "90px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              textAlign: "center",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              p: 1,
                            }}
                          >
                            Uploaded Image
                          </Box>
                        )}
                        {photo?.isUploading && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: "#0f8a6c" }}
                          >
                            Uploading...
                          </Typography>
                        )}
                        {!disabledAction && (
                          <CustomButton
                            title="Remove"
                            variant="outlined"
                            onClick={() => handleRemovePhoto(index)}
                            sx={{ mt: 1, width: "100%" }}
                          />
                        )}
                      </Box>
                    ),
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
          <SelectedSurveyItem />
          {!disabledAction && (
            <div style={{ textAlign: "left" }}>
              {/* <CustomButton
              title="Save"
              style={{ marginRight: "10px" }}
              onClick={handleSubmit}
              disabled={leadDetails?.lead?.status === "JOB" ? true : false}
            />{" "} */}
              <CustomButton
                disabled={
                  leadDetails?.lead?.status?.toUpperCase() === "COMPLETED" ||
                  leadDetails?.lead?.survey_status?.toUpperCase() ===
                    "COMPLETED"
                    ? true
                    : false
                }
                title="Complete"
                variant="outlined"
                onClick={handleCompleteSurvey}
              />
            </div>
          )}
        </section>
      </>
    );
  },
);
SurveyedSummaryTab.displayName = "SurveyedSummaryTab"; // <== important for forwardRef
export default SurveyedSummaryTab;
